import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { supabase } from '../../services/supabaseClient.js';

interface CaptchaProps {
  onUserInput: (input: string) => void;
}

export interface CaptchaRef {
  regenerate: () => void;
  getCaptchaData: () => { answer: string; token: string | null };
}

const Captcha = forwardRef<CaptchaRef, CaptchaProps>(({ onUserInput }, ref) => {
  const [question, setQuestion] = useState('Loading...');
  const [token, setToken] = useState<string | null>(null);
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const generateCaptcha = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase.functions.invoke('generate-captcha');
      if (error) throw error;
      setQuestion(data.question);
      setToken(data.token);
    } catch (err) {
      console.error('Failed to generate CAPTCHA:', err);
      setError('Failed to load security check. Please refresh.');
      setQuestion('Error');
    } finally {
      setLoading(false);
      setUserInput('');
      onUserInput('');
    }
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  useImperativeHandle(ref, () => ({
    regenerate: generateCaptcha,
    getCaptchaData: () => ({ answer: userInput, token }),
  }));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserInput(e.target.value);
    onUserInput(e.target.value);
  };

  return (
    <div className="bg-[#2B3139] p-4 rounded-lg border border-[#848E9C] my-4">
      <label htmlFor="captcha" className="block text-base font-medium text-white mb-2">
        Security Check: {question}
      </label>
      <div className="flex items-center">
        {loading ? (
          <div className="text-white">Loading...</div>
        ) : error ? (
          <div className="text-red-400">{error}</div>
        ) : (
          <>
            <input
              type="text"
              id="captcha"
              name="captcha"
              value={userInput}
              onChange={handleChange}
              className="shadow-sm focus:ring-[#F0B90B] focus:border-[#F0B90B] block w-28 p-3 text-lg border-[#848E9C] rounded-md bg-[#1E2329] text-white text-center font-bold transition-all duration-300 transform hover:scale-[1.05] focus:scale-[1.05]"
              placeholder="?"
              required
            />
            <button
              type="button"
              onClick={generateCaptcha}
              className="ml-4 inline-flex items-center p-3 border border-[#848E9C] shadow-sm text-xl font-medium rounded-md text-white bg-[#2B3139] hover:border-[#F0B90B] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F0B90B] transition-all duration-300 transform hover:scale-110 hover:rotate-12"
              title="Refresh CAPTCHA"
            >
              &#x21bb;
            </button>
          </>
        )}
      </div>
    </div>
  );
});

export default Captcha;
